/**
 * Helpers de formatação BR para o frontend (espelho parcial do FormatterService do backend).
 * Quando o backend manda formatted.* dentro da response, prefira usar.
 * Use esses helpers para campos calculados client-side.
 */

export function brl(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

/** Formata centavos (int) em BRL. Ex.: 4990 → R$ 49,90 */
export function brlFromCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '—'
  return brl(cents / 100)
}

/**
 * Arredonda um número para o múltiplo de `step` mais próximo (padrão 0,10).
 * Usado no detalhamento de comissões para "encaixar" o PERCENTUAL efetivo numa
 * escala de 0,10 e remover o ruído de casas decimais (o valor em R$ não é tocado).
 * Ex.: 8,02 → 8,00 · 7,99 → 8,00 · 8,01 → 8,00 · 14,99 → 15,00 · 8,06 → 8,10
 */
export function roundToStep(value: number | string | null | undefined, step = 0.1): number {
  const n = typeof value === 'string' ? Number(value) : value
  if (n === null || n === undefined || !Number.isFinite(n)) return 0
  const factor = Math.round(1 / step) // 0,10 → 10 (evita ruído de ponto flutuante no divisor)
  return Math.round(n * factor) / factor
}

export function dateBR(iso: string | null | undefined): string {
  if (!iso) return '—'
  // Datas "puras" (YYYY-MM-DD, sem hora) NÃO devem passar por `new Date()`:
  // o parser as trata como UTC meia-noite e, em fusos negativos (ex.: BRT −3),
  // exibe o dia anterior. Formatamos direto da string.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (dateOnly) {
    const [, y, m, d] = dateOnly
    return `${d}/${m}/${y}`
  }
  // ISO com hora (ex.: 2026-06-01T12:00:00-03:00) tem fuso embutido — converte ok.
  try {
    return new Date(iso).toLocaleDateString('pt-BR')
  } catch {
    return '—'
  }
}

export function dateTimeBR(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return '—'
  }
}

export function maskCpf(cpf: string | null | undefined): string {
  if (!cpf) return '—'
  const d = cpf.replace(/\D+/g, '')
  if (d.length !== 11) return cpf
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
}

export function maskCnpj(cnpj: string | null | undefined): string {
  if (!cnpj) return '—'
  const d = cnpj.replace(/\D+/g, '')
  if (d.length !== 14) return cnpj
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
}

/** Máscara progressiva de CPF (formata conforme o usuário digita). */
export function maskCpfPartial(value: string | null | undefined): string {
  const d = (value ?? '').replace(/\D+/g, '').slice(0, 11)
  if (d.length > 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`
  if (d.length > 6) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`
  if (d.length > 3) return `${d.slice(0, 3)}.${d.slice(3)}`
  return d
}

/** Máscara progressiva de CNPJ (formata conforme o usuário digita). */
export function maskCnpjPartial(value: string | null | undefined): string {
  const d = (value ?? '').replace(/\D+/g, '').slice(0, 14)
  if (d.length > 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`
  if (d.length > 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`
  if (d.length > 5) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`
  if (d.length > 2) return `${d.slice(0, 2)}.${d.slice(2)}`
  return d
}

export function maskDocument(doc: string | null | undefined): string {
  if (!doc) return '—'
  const d = doc.replace(/\D+/g, '')
  return d.length === 14 ? maskCnpj(d) : maskCpf(d)
}

export function maskPhone(phone: string | null | undefined): string {
  if (!phone) return '—'
  const d = phone.replace(/\D+/g, '')
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`
  return phone
}

/** Só dígitos (use ao enviar para o backend). */
export function onlyDigits(v: string | null | undefined): string {
  return (v ?? '').replace(/\D+/g, '')
}

/**
 * Máscara de telefone/WhatsApp para digitação ao vivo (10 ou 11 dígitos).
 * 10 → (11) 3333-4444 · 11 → (11) 99999-9999
 */
export function maskPhoneInput(v: string | null | undefined): string {
  const d = onlyDigits(v).slice(0, 11)
  if (d.length <= 10) return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2')
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2')
}

export function maskCep(cep: string | null | undefined): string {
  if (!cep) return '—'
  const d = cep.replace(/\D+/g, '')
  if (d.length !== 8) return cep
  return `${d.slice(0, 5)}-${d.slice(5)}`
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?'
}
